import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION, EddYesNo } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types';
import { reportError, ObservabilityErrorSeverity } from '@Services/observability/initObservability';

export const useCashPaymentsLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');
  const { t: tValidation } = useTranslation('enJSON');

  const formMethods = useFormHook({
    schemaTitle: 'cash-payments',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_CASH_PAYMENTS]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_CASH_PAYMENTS) || EddYesNo.NO,
      [EddEmbeddedFields.EDD_CASH_PAYMENTS_PERCENTAGE]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_CASH_PAYMENTS_PERCENTAGE) ?? null,
    },
    formSchema: z
      .object({
        [EddEmbeddedFields.EDD_CASH_PAYMENTS]: z
          .string()
          .min(1, { message: tValidation('validation.required') }),
        [EddEmbeddedFields.EDD_CASH_PAYMENTS_PERCENTAGE]: z.number().nullable().optional(),
      })
      .superRefine((data, ctx) => {
        if (data[EddEmbeddedFields.EDD_CASH_PAYMENTS] === EddYesNo.YES) {
          const percentage = data[EddEmbeddedFields.EDD_CASH_PAYMENTS_PERCENTAGE];
          if (percentage === null || percentage === undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tValidation('validation.required'),
              path: [EddEmbeddedFields.EDD_CASH_PAYMENTS_PERCENTAGE],
            });
          } else if (percentage < 0 || percentage > 100) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('organic.cashPayments.validation.percentageRange'),
              path: [EddEmbeddedFields.EDD_CASH_PAYMENTS_PERCENTAGE],
            });
          }
        }
      }),
  });

  const handleSubmit = async () => {
    const cashPaymentsValue = formMethods.getValues(EddEmbeddedFields.EDD_CASH_PAYMENTS);
    const percentageValue = formMethods.getValues(EddEmbeddedFields.EDD_CASH_PAYMENTS_PERCENTAGE);

    saveEddQuestionToLocalStore({
      question: t('organic.cashPayments.title'),
      questionId: EddEmbeddedFields.EDD_CASH_PAYMENTS,
      answer: JSON.stringify(cashPaymentsValue),
    });

    if (
      cashPaymentsValue === EddYesNo.YES &&
      percentageValue !== null &&
      percentageValue !== undefined
    ) {
      saveEddQuestionToLocalStore({
        question: t('organic.cashPayments.percentagePrompt'),
        questionId: EddEmbeddedFields.EDD_CASH_PAYMENTS_PERCENTAGE,
        answer: JSON.stringify(percentageValue),
      });
    }

    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_CASH_PAYMENTS);
    } catch (error) {
      reportError({
        severity: ObservabilityErrorSeverity.ERROR,
        metadata: {
          name: 'EddCashPaymentsSubmitError',
          message: error instanceof Error ? error.message : String(error),
        },
        groupingHash: 'edd-submit-error',
      });
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
